import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ChatConfig, Message, Thread, MattressProduct, MessageContent, ProductRecommendationResponse } from '../types';
import { fetchConfig, sendChatMessage, endSession as apiEndSession } from '../services/api';
import { useEffect } from 'react';
import { getMerchantIdFromHostname } from '../utils/merchantUtils';

// Add TypeScript extensions for debug globals
declare global {
  interface Window {
    __MATTRESSAI_DEBUG_CONFIG?: any;
    __MATTRESSAI_DEBUG_THREAD?: string;
  }
}

// ========== Constants ==========
/**
 * Base URL for the Cloudflare Worker that handles API requests
 */
const WORKER_URL = 'https://mattressai2-chat.blake-dac.workers.dev';

/**
 * Time in milliseconds before a session times out due to inactivity
 */
const SESSION_TIMEOUT = 7 * 60 * 1000; // 7 minutes inactivity timeout

/**
 * Maximum age of a thread before it's considered expired and reset
 */
const THREAD_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // One week in milliseconds - max thread age before forced reset

// ========== Default Configuration ==========
/**
 * Default chat configuration used when no merchant-specific config is available
 */
const DEFAULT_CONFIG: ChatConfig = {
  storeInfo: {
    name: 'MattressAI Chat',
    hours: '9AM-5PM EST',
    hasMultipleLocations: false,
    contactInfo: 'support@mattressai.co'
  },
  chatConfig: {
    welcomeMessage: 'Welcome! I\'m your personal mattress shopping assistant.',
    initialGreeting: 'Hi! I\'m here to help you find your perfect mattress. What brings you in today?',
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 2000,
    assistantId: '',
    toneDirective: 'friendly and helpful',
    conversationStyle: 'consultative'
  },
  branding: {
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      background: '#f8fafc',
      text: '#1e293b'
    },
    fonts: {
      primary: 'Inter',
      secondary: 'system-ui'
    },
    logo: 'https://firebasestorage.googleapis.com/v0/b/mattressai2.appspot.com/o/Untitled%20design%20(2).png?alt=media&token=e205d8e2-0780-47db-8b45-f81bd8ee2465',
    chatTitle: 'MattressAI Chat'
  }
};

// ========== Utility Functions ==========

/**
 * Create a simple logger for consistent and controlled logging
 */
const DEBUG_MODE = true;
const logger = {
  debug: (message: string, ...args: any[]) => {
    if (DEBUG_MODE) {
      console.log(`[ChatStore Debug] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]) => {
    if (DEBUG_MODE) {
      console.info(`[ChatStore] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`[ChatStore Warning] ${message}`, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(`[ChatStore Error] ${message}`, ...args);
  }
};

/**
 * Ensure all messages have proper Date objects for timestamps
 * Converts string timestamps to Date objects for consistent handling
 * @param messages - Array of messages to process
 * @returns Array of messages with proper Date timestamps
 */
const ensureDateTimestamps = (messages: Message[]): Message[] => {
  return messages.map(message => ({
    ...message,
    timestamp: message.timestamp instanceof Date ? 
      message.timestamp : 
      new Date(message.timestamp || Date.now())
  }));
};

/**
 * Check if a thread has expired based on creation time
 * Used to determine if a thread should be reset
 * @param threadCreationTime - Timestamp when the thread was created
 * @returns True if the thread has expired, false otherwise
 */
const hasThreadExpired = (threadCreationTime: number | null): boolean => {
  if (!threadCreationTime) return false;
  
  const now = Date.now();
  const threadAge = now - threadCreationTime;
  return threadAge > THREAD_MAX_AGE;
};

/**
 * Check if the page was refreshed
 * Uses multiple methods to reliably detect page refreshes across browsers
 * @returns True if the page was refreshed, false for initial loads
 */
const wasPageRefreshed = () => {
  // Check for reset parameter in URL
  const refreshParam = new URLSearchParams(window.location.search).get('reset');
  if (refreshParam === 'true') return true;
  
  // Use the most reliable browser API first
  if (window.performance) {
    // For browsers with newer Performance API
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0 && entries[0] instanceof PerformanceNavigationTiming) {
      return (entries[0] as PerformanceNavigationTiming).type === 'reload';
    }
    
    // Fallback for older browsers
    if (window.performance.navigation) {
      return window.performance.navigation.type === 1; // 1 = reload
    }
  }
  
  return false;
};

/**
 * Clear all chat-related storage
 * Used when resetting the chat or when a session ends
 */
const clearChatStorage = () => {
  // Clear all sessionStorage items related to chat
  sessionStorage.removeItem('threadId');
  sessionStorage.removeItem('sessionId');
  sessionStorage.removeItem('lastActivityTime');
  sessionStorage.removeItem('mattressai-chat-store');
  sessionStorage.removeItem('page-load-timestamp');
  
  // Log for debugging purposes
  console.log('[MattressAI] Chat storage cleared', new Date().toISOString());
};

// ========== Types ==========
/**
 * State and actions for the chat store
 * Manages messages, configuration, and session state
 */
interface ChatStore {
  // State
  messages: Message[];               // All chat messages
  config: ChatConfig | null;         // Merchant configuration
  isLoading: boolean;                // Loading state for API calls
  threadId: string | null;           // Current chat thread ID
  error: string | null;              // Error message if any
  thread: Thread | null;             // Full thread object
  sessionId: string | null;          // Current session ID
  threadCreationTime: number | null; // When the thread was created
  sessionActive: boolean;            // Whether session is active
  sessionTimeoutId: NodeJS.Timeout | null; // Timeout for session inactivity
  lastActivityTime: string;          // Last user activity timestamp
  actualMerchantId: string | null;   // Merchant ID for API calls
  visibilityChangeHandlerSet: boolean; // Track if visibility change handler is set up
  isSessionEnding: boolean;          // Track if session is ending

  // Message actions
  addMessage: (message: Message) => void;                  // Add a message to the chat
  removeMessage: (messageId: string) => void;              // Remove a message by ID
  clearMessages: () => void;                               // Clear all messages
  sendMessage: (content: string) => Promise<void>;         // Send a message to the API
  fixMessageTimestamps: () => void;                        // Fix timestamp formats
  handleProductSelect: (product: MattressProduct) => void; // Handle product selection

  // Session & thread actions
  setThreadId: (threadId: string) => void;       // Set thread ID
  setSessionId: (sessionId: string) => void;     // Set session ID
  startSession: () => void;                      // Start a new session
  endSession: (reason?: string) => void;         // End the current session
  resetSessionTimer: () => void;                 // Reset inactivity timer
  resetChat: () => void;                         // Reset the entire chat
  
  // Configuration actions
  fetchConfig: () => Promise<void>;              // Fetch merchant configuration
}

// ========== Store Implementation ==========
/**
 * Main chat store using Zustand
 * Implements persistence to keep chat state across page reloads
 */
export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      // ===== State =====
      messages: [],
      config: null,
      isLoading: false,
      threadId: null,
      sessionId: null,
      threadCreationTime: null,
      error: null,
      thread: null,
      sessionActive: false,
      sessionTimeoutId: null,
      lastActivityTime: new Date().toISOString(),
      actualMerchantId: null,
      visibilityChangeHandlerSet: false,
      isSessionEnding: false,
      
      // ===== Message Actions =====
      
      /**
       * Add a message to the chat
       * Ensures proper timestamp formatting and scrolls to bottom
       */
      addMessage: (message: Message) => {
        // Ensure timestamp is a Date object
        const messageWithDate = {
          ...message,
          timestamp: message.timestamp instanceof Date ? 
            message.timestamp : 
            new Date(message.timestamp || Date.now())
        };
        
        set(state => ({
          messages: [...state.messages, messageWithDate],
          lastActivityTime: new Date().toISOString()
        }));
        
        // Reset session timer when a message is added
        get().resetSessionTimer();
      },
      
      removeMessage: (messageId: string) => {
        set(state => ({
          messages: state.messages.filter(msg => msg.id !== messageId)
        }));
      },
      
      /**
       * Clear all messages from the chat but keep the session active
       * This is used when refreshing the page to give the user a fresh start
       */
      clearMessages: () => {
        // Get current thread and session IDs
        const { threadId, sessionId } = get();
        
        // Only clear messages if we have a valid session
        // This prevents clearing on initial page load
        if (threadId && sessionId) {
          console.log('[MattressAI] Clearing chat messages while preserving session');
          set({ 
            messages: [],
            isLoading: false,
            error: null
          });
          
          // Reset session timer
          get().resetSessionTimer();
        }
      },
      
      sendMessage: async (content: string) => {
        try {
          // Add the user message to the state
          const userMessage: Message = {
            id: `msg_${Date.now()}`,
            content,
            role: 'user',
            timestamp: new Date()
          };
          
          // Update state to show loading and add user message
          set(state => ({ 
            messages: [...state.messages, userMessage],
            isLoading: true,
            error: null
          }));
          
          const { threadId, sessionId } = get();
          
          // Additional metadata to send with the message
          const metadata = {
            pageUrl: window.location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            timestamp: new Date().toISOString(),
            utmSource: new URLSearchParams(window.location.search).get('utm_source'),
            utmMedium: new URLSearchParams(window.location.search).get('utm_medium'),
            utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign')
          };
          
          // Call the API with the message
          const response = await sendChatMessage(
            content,
            threadId || undefined,
            sessionId || undefined,
            metadata
          );
          
          // Update thread ID if we got a new one
          if (response.threadId && (!threadId || response.isNewThread)) {
            get().setThreadId(response.threadId);
          }
          
          // Update session ID if we got a new one
          if (response.sessionId && !sessionId) {
            get().setSessionId(response.sessionId);
          }
          
          // Create the assistant message from the response
          const assistantMessage: Message = {
            id: `msg_${Date.now() + 1}`,
            content: response.message || response.content as string | MessageContent,
            role: 'assistant',
            timestamp: new Date()
          };
          
          // Add assistant message to state
          set(state => ({
            messages: [...state.messages, assistantMessage],
            isLoading: false
          }));
          
          // Reset the session timer after receiving a message
          get().resetSessionTimer();
          
        } catch (error) {
          // Handle errors
          set({ 
            isLoading: false,
            error: error instanceof Error ? error.message : 'An unknown error occurred'
          });
          
          // Special handling for thread not found errors - reset the thread
          if (error instanceof Error && 
              (error.message.includes('Thread not found') || 
               error.message.includes('No thread found'))) {
            // Clear the threadId and associated state
            sessionStorage.removeItem('threadId');
            set({ 
              threadId: null,
              threadCreationTime: null 
            });
          }
        }
      },
      
      fixMessageTimestamps: () => {
        set(state => ({
          messages: ensureDateTimestamps(state.messages)
        }));
      },
      
      handleProductSelect: (product: MattressProduct) => {
        // Mark that the user selected a product
        const message: Message & { metadata?: { type: string, productId: string, productName: string } } = {
          id: `msg_selection_${Date.now()}`,
          content: `I'm interested in the ${product.name}`,
          role: 'user',
          timestamp: new Date(),
          metadata: {
            type: 'product_selection',
            productId: product.id,
            productName: product.name
          }
        };
        
        // Add the message
        get().addMessage(message);
        
        // Send an actual message to continue the conversation 
        get().sendMessage(`I'd like to learn more about the ${product.name}. Can you tell me about its features?`);
      },
      
      // ===== Session & Thread Actions =====
      
      setThreadId: (threadId: string) => {
        // sessionStorage.setItem('threadId', threadId); // Handled by persist middleware
        set({ 
          threadId,
          threadCreationTime: Date.now() // Keep setting creation time here
        });
      },
      
      setSessionId: (sessionId: string) => {
        // sessionStorage.setItem('sessionId', sessionId); // Handled by persist middleware
        set({ sessionId });
      },
      
      startSession: () => {
        const refreshDetected = wasPageRefreshed();
        console.log('[MattressAI] Page refresh detected:', refreshDetected);
        
        // If this is a page refresh, clear messages but keep the session
        if (refreshDetected) {
          const { threadId, sessionId } = get();
          if (threadId && sessionId) {
            console.log('[MattressAI] Page refreshed - clearing messages but keeping session');
            set({ 
              messages: [],
              isLoading: false,
              error: null,
              lastActivityTime: new Date().toISOString()
            });
            get().resetSessionTimer();
            
            // Fetch config to ensure branding is applied
            get().fetchConfig();
            return;
          }
        }
        
        // For normal starts (not refreshes)
        console.log('[MattressAI] Starting new chat session');
        
        set({ sessionActive: true });
        get().resetSessionTimer();
      },
      
      resetChat: () => {
        // Clear all session-related state
        set({
          messages: [],
          threadId: null,
          sessionId: null,
          threadCreationTime: null,
          error: null,
          sessionActive: false,
          sessionTimeoutId: null,
          // Optionally reset config back to null or default?
          // config: null, 
        });
        
        // Clear persisted storage using the middleware's method
        // Note: Ensure persist middleware is configured correctly for this.
        useChatStore.persist.clearStorage();
        console.log('[MattressAI] Chat storage cleared via persist middleware.');

        // Removed history manipulation - likely unnecessary
      },
      
      endSession: async (reason = 'user_closed') => {
        try {
          const { sessionId, threadId, config } = get();
          
          // Don't proceed if session ID or thread ID is missing
          if (!sessionId || !threadId) {
            console.warn('[MattressAI] Cannot end session: missing sessionId or threadId');
            
            // Still reset the chat state to avoid a stuck UI
            get().resetChat();
            return;
          }
          
          // Track that we're ending the session
          console.log('[MattressAI] Ending session', { sessionId, threadId, reason });
          
          // Get merchantId from config or fallback to subdomain
          const merchantId = config?.merchantId || getMerchantIdFromHostname();
          if (!merchantId) {
            console.error('[MattressAI] Cannot end session: merchantId not available');
            get().resetChat();
            return;
          }
          
          // Set the session status to ending to prevent duplicate end requests
          set({ isSessionEnding: true });

          // Maximum retries for ending a session
          const maxRetries = 3;
          const baseDelay = 1000; // 1 second
          let attempt = 0;
          let lastError = null;
          
          while (attempt < maxRetries) {
            attempt++;
            try {
              // End the session via API with structured response
              const result = await apiEndSession(
                sessionId,
                threadId,
                merchantId,
                new Date().toISOString(),
                reason
              );
              
              // Handle the response based on success status
              if (result.success) {
                console.log(`[MattressAI] Session ended successfully: ${result.message || 'OK'}`, { 
                  sessionId, 
                  threadId,
                  code: result.code,
                  attempt
                });
                
                // Reset chat state on success
                get().resetChat();
                return;
              }
              
              // Special status handling
              if (result.status === 'pending') {
                console.log('[MattressAI] Session end is pending, waiting before retry');
                await new Promise(resolve => setTimeout(resolve, baseDelay * attempt));
                continue;
              }
              
              // For certain error codes, we might want to take specific actions
              if (result.code === 'SESSION_NOT_FOUND' || result.code === 'ALREADY_COMPLETED') {
                console.log(`[MattressAI] Session ${result.code}, treating as ended`);
                get().resetChat();
                return;
              }
              
              // For other errors, retry with backoff
              lastError = new Error(result.error || 'Unknown error');
              console.warn(`[MattressAI] Failed to end session (attempt ${attempt}):`, result.error);
              
              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
              }
            } catch (error) {
              lastError = error;
              console.error(`[MattressAI] Error ending session (attempt ${attempt}):`, error);
              
              if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
              }
            }
          }
          
          // If we get here, all retries failed
          console.error('[MattressAI] Failed to end session after all retries:', lastError);
          
          // Store the failed session info for potential recovery
          try {
            localStorage.setItem('failed_session_end', JSON.stringify({
              sessionId,
              threadId,
              merchantId,
              timestamp: Date.now(),
              reason
            }));
          } catch (storageError) {
            console.error('[MattressAI] Failed to store failed session info:', storageError);
          }
          
          // Reset chat state even if ending failed
          get().resetChat();
        } catch (error) {
          console.error('[MattressAI] Unexpected error ending session:', error);
          get().resetChat();
        } finally {
          set({ isSessionEnding: false });
        }
      },
      
      resetSessionTimer: () => {
        // Clear existing timer first
        const currentTimeoutId = get().sessionTimeoutId;
        if (currentTimeoutId !== null) {
          clearTimeout(currentTimeoutId);
        }
        
        // Record last activity in a single place
        const now = new Date().toISOString();
        set({ lastActivityTime: now });
        
        // Use a simple timeout if document is visible
        if (document.visibilityState === 'visible') {
          const newTimeoutId = setTimeout(() => {
            console.log('[MattressAI] Session timeout reached while page visible, ending session');
            get().endSession('timeout');
          }, SESSION_TIMEOUT);
          
          set({ sessionTimeoutId: newTimeoutId });
        } else {
          // Use a shorter timeout if page isn't visible (background tab)
          const backgroundTimeoutId = setTimeout(() => {
            console.log('[MattressAI] Session timeout reached while page in background, ending session');
            get().endSession('background_timeout');
          }, SESSION_TIMEOUT / 2); // Use half the timeout for background tabs
          
          set({ sessionTimeoutId: backgroundTimeoutId });
        }
        
        // Also set up visibility change handler if not already done
        if (!get().visibilityChangeHandlerSet) {
          document.addEventListener('visibilitychange', () => {
            // When visibility changes, reset the timer
            get().resetSessionTimer();
          });
          
          // Track that we've set up the handler to avoid duplication
          set({ visibilityChangeHandlerSet: true });
        }
      },
      
      // ===== Configuration Actions =====
      
      fetchConfig: async () => {
        try {
          set({ isLoading: true });
          
          // Check if thread has expired
          const { threadCreationTime } = get();
          if (hasThreadExpired(threadCreationTime)) {
            // Reset the thread if it has expired
            sessionStorage.removeItem('threadId');
            set({ 
              threadId: null,
              threadCreationTime: null
            });
          }
          
          // Fetch the configuration from API
          const configResponse = await fetchConfig();
          
          // Extract the config data and thread ID
          // With the updated API, fetchConfig might return different formats
          let configData = configResponse as ChatConfig;
          let threadId: string | null = null;
          
          logger.debug('Inspecting config response:', { 
            type: typeof configResponse,
            hasData: configResponse && typeof configResponse === 'object' && 'data' in configResponse,
            keys: configResponse && typeof configResponse === 'object' ? Object.keys(configResponse) : [],
            hasThread: configResponse && typeof configResponse === 'object' && 'thread' in configResponse,
            threadValue: (configResponse as any)?.thread
          });
          
          // NEW API FORMAT: response may have thread property added by api.ts
          if (configResponse && typeof configResponse === 'object' && 'thread' in configResponse) {
            const responseThread = (configResponse as any).thread;
            if (typeof responseThread === 'string') {
              threadId = responseThread;
              logger.info('Found thread ID in config response:', threadId);
            }
          }
          // OLDER API FORMAT: { success: true, data: { config: {...}, thread: "..." } }
          else if (configResponse && typeof configResponse === 'object') {
            // Check for data.config structure (old format)
            if ('data' in configResponse && 
                configResponse.data && 
                typeof configResponse.data === 'object' && 
                'config' in configResponse.data) {
              
              configData = (configResponse.data as any).config as ChatConfig;
              logger.info('Using nested config from data.config structure');
              
              // Extract thread ID from data.thread
              if ('thread' in configResponse.data) {
                const responseThread = (configResponse.data as any).thread;
                if (typeof responseThread === 'string') {
                  threadId = responseThread;
                  logger.info('Extracted thread ID from data.thread:', threadId);
                }
              }
            }
          }
          
          // Set debug globals to help with troubleshooting
          if (typeof window !== 'undefined') {
            window.__MATTRESSAI_DEBUG_CONFIG = configData;
            window.__MATTRESSAI_DEBUG_THREAD = threadId;
          }
          
          // Extract the actual merchantId if available
          let actualMerchantId = null;
          if (configData?.merchantId) {
            actualMerchantId = configData.merchantId;
          }
          
          // Update state with thread ID if available
          if (threadId) {
            set({
              threadId: threadId as string,
              threadCreationTime: Date.now()
            });
            // Also store in sessionStorage as backup
            sessionStorage.setItem('threadId', threadId);
          }
          
          // Update the state with the fetched config
          set({
            config: configData,
            actualMerchantId,
            isLoading: false
          });
          
          // Send initial greeting if no messages
          const { messages } = get();
          if (messages.length === 0 && configData?.chatConfig?.initialGreeting) {
            // Create the initial greeting message
            const initialMessage: Message = {
              id: 'initial-greeting',
              content: configData.chatConfig.initialGreeting,
              role: 'assistant',
              timestamp: new Date()
            };
            
            // Add the message
            set(state => ({
              messages: [...state.messages, initialMessage]
            }));
          }
          
        } catch (error) {
          // Set a default config on error
          set({
            config: DEFAULT_CONFIG,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch configuration'
          });
        }
      }
    }),
    {
      /**
       * Storage key for persisted state in sessionStorage
       */
      name: 'mattressai-chat-store',
      
      /**
       * Use sessionStorage explicitly
       */
      storage: createJSONStorage(() => sessionStorage),
      
      /**
       * Selectively persist only necessary state values
       * Avoids persisting transient state like loading flags
       */
      partialize: (state) => ({
        messages: state.messages,
        threadId: state.threadId,
        sessionId: state.sessionId,
        threadCreationTime: state.threadCreationTime,
        // Persist lastActivityTime if needed for cross-session checks?
        // lastActivityTime: state.lastActivityTime 
      }) as unknown as ChatStore, // Need casting because partial state doesn't match full type
      
      /**
       * Callback triggered after store is rehydrated from storage
       * Used to fix timestamp formats after rehydration
       */
      onRehydrateStorage: () => (state) => {
        // Fix timestamps after rehydration
        if (state && state.messages) {
          // Ensure the action exists before calling
          state.fixMessageTimestamps?.(); 
        }
      }
    }
  )
);

// ========== Custom Hooks ==========

/**
 * Helper hook to fix timestamps after store rehydration
 * 
 * When the store is rehydrated from sessionStorage, the Date objects are
 * converted to strings. This hook ensures they're converted back to Date
 * objects for proper display and sorting in the UI.
 * 
 * It also handles URL cleanup by removing the 'reset' parameter after
 * processing to keep the URL clean.
 */
export function useFixChatTimestamps() {
  const fixMessageTimestamps = useChatStore(state => state.fixMessageTimestamps);
  
  useEffect(() => {
    // Fix timestamps on initial load
    fixMessageTimestamps();
    
    // If the URL has a reset parameter, clear it after processing
    if (window.location.search.includes('reset=true')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      window.history.replaceState({}, '', url.toString());
    }
  }, [fixMessageTimestamps]);
} 